import { useState, useEffect, useRef, useMemo } from "react";
import { X, ChevronRight, Check } from "lucide-react";
import { Category } from "./CategoryMegaMenu";
import { Button } from "../shadcn/ui/Button";
import { CategorySearchInput } from "./CategorySearchInput";
import {
  findCategoryPath,
  isValidCategoryDepth,
  getCategoryDisplayPath,
  requiresLevel3Selection,
  searchCategories,
} from "../utils/categoryUtils";

export interface CategoryMegaMenuPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: Category[];
  selectedCategoryId?: string;
  onConfirm: (categoryId: string, categoryPath: Category[]) => void;
  onCancel?: () => void;
  loading?: boolean;
  /** Seller flow: first column lists membership industries only. */
  industrySelectionMode?: boolean;
}

export const CategoryMegaMenuPopup = ({
  open,
  onOpenChange,
  categories,
  selectedCategoryId,
  onConfirm,
  onCancel,
  loading = false,
  industrySelectionMode = false,
}: CategoryMegaMenuPopupProps) => {
  const [selectedLevel1, setSelectedLevel1] = useState<Category | null>(null);
  const [selectedLevel2, setSelectedLevel2] = useState<Category | null>(null);
  const [selectedLevel3, setSelectedLevel3] = useState<Category | null>(null);
  const [tempSelectedCategoryId, setTempSelectedCategoryId] = useState<
    string | null
  >(null);
  const [searchQuery, setSearchQuery] = useState("");
  const popupRef = useRef<HTMLDivElement>(null);

  // Initialize selected path from selectedCategoryId
  useEffect(() => {
    if (open && selectedCategoryId && categories.length > 0) {
      const path = findCategoryPath(categories, selectedCategoryId);
      if (path && path.length > 0) {
        setSelectedLevel1(path[0]);
        if (path.length > 1) {
          setSelectedLevel2(path[1]);
        } else {
          setSelectedLevel2(null);
        }
        if (path.length > 2) {
          setSelectedLevel3(path[2]);
        } else {
          setSelectedLevel3(null);
        }
        setTempSelectedCategoryId(selectedCategoryId);
      }
    } else if (open) {
      // Reset when opening without preselection
      setSelectedLevel1(null);
      setSelectedLevel2(null);
      setSelectedLevel3(null);
      setTempSelectedCategoryId(null);
    }
  }, [open, selectedCategoryId, categories]);

  // Reset selection and search when popup closes
  useEffect(() => {
    if (!open) {
      setSelectedLevel1(null);
      setSelectedLevel2(null);
      setSelectedLevel3(null);
      setTempSelectedCategoryId(null);
      setSearchQuery("");
    }
  }, [open]);

  // Filter Level-1 categories based on search query (searches through all levels)
  const filteredCategories = useMemo(() => {
    return searchCategories(categories, searchQuery);
  }, [categories, searchQuery]);

  // Handle Level-1 selection
  const handleLevel1Select = (category: Category) => {
    setSelectedLevel1(category);
    setSelectedLevel2(null);
    setSelectedLevel3(null);
    setTempSelectedCategoryId(null);
  };

  // Handle Level-2 selection
  const handleLevel2Select = (category: Category) => {
    setSelectedLevel2(category);
    setSelectedLevel3(null);

    const needsLevel3 = requiresLevel3Selection(categories, category.id);
    setTempSelectedCategoryId(needsLevel3 ? null : category.id);
  };

  // Handle Level-3 selection
  const handleLevel3Select = (category: Category) => {
    setSelectedLevel3(category);
    setTempSelectedCategoryId(category.id);
  };

  const buildPathFromLevels = (): Category[] | null => {
    if (!selectedLevel1) return null;
    if (selectedLevel3 && selectedLevel2) {
      return [selectedLevel1, selectedLevel2, selectedLevel3];
    }
    if (selectedLevel2) {
      return [selectedLevel1, selectedLevel2];
    }
    return [selectedLevel1];
  };

  const resolvedCategoryId = useMemo(() => {
    if (!selectedLevel1) return null;
    if (selectedLevel3?.id) return String(selectedLevel3.id);
    if (selectedLevel2?.id) {
      if (requiresLevel3Selection(categories, selectedLevel2.id)) {
        return null;
      }
      return String(selectedLevel2.id);
    }
    return null;
  }, [selectedLevel1, selectedLevel2, selectedLevel3, categories]);

  // Get Level-2 categories for selected Level-1 (filtered by search query)
  const level2Categories = useMemo(() => {
    if (!selectedLevel1?.id) {
      return [];
    }

    const path = findCategoryPath(categories, selectedLevel1.id);
    const level1Node = path?.[path.length - 1];
    const children = level1Node?.children ?? selectedLevel1.children ?? [];
    if (!children.length) {
      return [];
    }

    // If no search query, return all Level-2 categories
    if (!searchQuery.trim()) {
      return children;
    }

    // Filter Level-2 categories based on search query
    const query = searchQuery.toLowerCase().trim();
    return children.filter((level2) => {
      // Check if Level-2 name matches
      const nameMatches = level2.name.toLowerCase().includes(query);

      // Check if any Level-3 children match
      const hasMatchingChildren =
        level2.children && level2.children.length > 0
          ? level2.children.some((level3) =>
              level3.name.toLowerCase().includes(query)
            )
          : false;

      // Include if name matches OR has matching children
      return nameMatches || hasMatchingChildren;
    });
  }, [selectedLevel1, categories, searchQuery]);

  // Get Level-3 categories for selected Level-2 (filtered by search query)
  const level3Categories = useMemo(() => {
    if (!selectedLevel2?.id) {
      return [];
    }

    const path = findCategoryPath(categories, selectedLevel2.id);
    const level2Node = path?.[path.length - 1];
    const children =
      level2Node?.children ?? selectedLevel2.children ?? [];
    if (!children.length) {
      return [];
    }

    // If no search query, return all Level-3 categories
    if (!searchQuery.trim()) {
      return children;
    }

    // Filter Level-3 categories based on search query
    const query = searchQuery.toLowerCase().trim();

    // Check if Level-2 name itself matches (if so, show all Level-3 even if they don't match)
    const level2NameMatches = selectedLevel2.name.toLowerCase().includes(query);

    // If Level-2 name matches, show all Level-3; otherwise filter Level-3
    if (level2NameMatches) {
      return children;
    }

    // Filter Level-3 by name
    return children.filter((level3) =>
      level3.name.toLowerCase().includes(query)
    );
  }, [selectedLevel2, categories, searchQuery]);

  // Check if current selection is valid (Industry + Product, or + Sub product when required)
  const isValidSelection = useMemo(() => {
    if (!resolvedCategoryId) {
      return false;
    }
    return isValidCategoryDepth(categories, resolvedCategoryId);
  }, [resolvedCategoryId, categories]);

  const needsLevel3ForCurrent = Boolean(
    selectedLevel2?.id &&
      requiresLevel3Selection(categories, selectedLevel2.id) &&
      !selectedLevel3,
  );

  // Get selected path for display
  const selectedPath = useMemo(() => {
    if (!resolvedCategoryId) {
      return null;
    }
    return (
      findCategoryPath(categories, resolvedCategoryId) ?? buildPathFromLevels()
    );
  }, [resolvedCategoryId, categories, selectedLevel1, selectedLevel2, selectedLevel3]);

  // Get display path string
  const displayPath = useMemo(() => {
    if (selectedPath?.length) {
      return selectedPath.map((c) => c.name).join(" > ");
    }
    if (!resolvedCategoryId) {
      return "";
    }
    return getCategoryDisplayPath(categories, resolvedCategoryId);
  }, [selectedPath, resolvedCategoryId, categories]);

  // Keep temp id aligned with resolved selection for legacy checks in the template
  useEffect(() => {
    setTempSelectedCategoryId(resolvedCategoryId);
  }, [resolvedCategoryId]);

  // Handle confirm
  const handleConfirm = () => {
    if (!isValidSelection || !resolvedCategoryId) {
      return;
    }
    const path =
      findCategoryPath(categories, resolvedCategoryId) ?? buildPathFromLevels();
    if (!path?.length) {
      return;
    }
    onConfirm(resolvedCategoryId, path);
    onOpenChange(false);
  };

  // Handle cancel
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        handleCancel();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [open]);

  // Prevent body scroll when popup is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
        onClick={handleCancel}
      />

      {/* Popup */}
      <div
        ref={popupRef}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900">
                Select Category
              </h2>
              {displayPath && (
                <p className="text-sm text-gray-600 mt-1">
                  Selected:{" "}
                  <span className="font-medium text-orange-600">
                    {displayPath}
                  </span>
                </p>
              )}
              {needsLevel3ForCurrent && (
                <p className="text-sm text-amber-700 mt-1">
                  This product has sub-products — select one in the third column.
                </p>
              )}
              {!isValidSelection && selectedLevel2 && !needsLevel3ForCurrent && (
                <p className="text-sm text-red-600 mt-1">
                  Please select a valid product category
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="ml-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content - Three Columns */}
          <div className="flex-1 overflow-hidden flex">
            {/* Column 1: Level-1 Categories */}
            <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col">
              {/* Sticky Header with Search */}
              <div className="p-4 pb-3 bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  {industrySelectionMode ? "Industries" : "Main Categories"}
                </h3>
                {/* Search Box */}
                <CategorySearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  className="mb-0"
                />
              </div>
              {/* Scrollable Category List */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4 pt-3">
                  <div className="space-y-1">
                    {filteredCategories.length > 0 ? (
                      filteredCategories.map((category) => {
                        const isSelected = selectedLevel1?.id === category.id;
                        return (
                          <button
                            type="button"
                            key={category.id}
                            onClick={() => handleLevel1Select(category)}
                            className={`w-full text-left px-4 py-3 rounded-md transition-colors ${
                              isSelected
                                ? "bg-orange-500 text-white font-medium"
                                : "text-gray-700 hover:bg-orange-100"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm">{category.name}</span>
                              {isSelected && <Check className="h-4 w-4" />}
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No categories found
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Column 2: Level-2 Categories */}
            <div className="w-80 flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
              {selectedLevel1 ? (
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                    {selectedLevel1.name} - Subcategories
                  </h3>
                  {level2Categories.length > 0 ? (
                    <div className="grid grid-cols-2 gap-3">
                      {level2Categories.map((category) => {
                        const isSelected = selectedLevel2?.id === category.id;
                        const hasChildren =
                          category.children && category.children.length > 0;
                        return (
                          <button
                            type="button"
                            key={category.id}
                            onClick={() => handleLevel2Select(category)}
                            className={`relative p-4 rounded-lg border-2 transition-all ${
                              isSelected
                                ? "border-orange-500 bg-orange-50"
                                : "border-gray-200 hover:border-orange-300 hover:bg-orange-50/50"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-900">
                                {category.name}
                              </span>
                              {isSelected && (
                                <Check className="h-4 w-4 text-orange-600" />
                              )}
                            </div>
                            {hasChildren && (
                              <div className="mt-2 text-xs text-gray-500">
                                {category.children?.length} subcategories
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      {searchQuery.trim()
                        ? "No subcategories found matching your search"
                        : "No subcategories available"}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  Select a main category
                </div>
              )}
            </div>

            {/* Column 3: Level-3 Categories */}
            <div className="flex-1 bg-white overflow-y-auto">
              {selectedLevel2 ? (
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                    {selectedLevel1?.name} {">"} {selectedLevel2.name} -
                    Sub-subcategories
                  </h3>
                  {level3Categories.length > 0 ? (
                    <div className="grid grid-cols-3 gap-3">
                      {level3Categories.map((category) => {
                        const isSelected = selectedLevel3?.id === category.id;
                        return (
                          <button
                            type="button"
                            key={category.id}
                            onClick={() => handleLevel3Select(category)}
                            className={`relative p-4 rounded-lg border-2 transition-all ${
                              isSelected
                                ? "border-orange-500 bg-orange-50"
                                : "border-gray-200 hover:border-orange-300 hover:bg-orange-50/50"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-900">
                                {category.name}
                              </span>
                              {isSelected && (
                                <Check className="h-4 w-4 text-orange-600" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      {searchQuery.trim() ? (
                        <div className="text-gray-500 text-sm">
                          No sub-subcategories found matching your search
                        </div>
                      ) : requiresLevel3Selection(
                          categories,
                          selectedLevel2.id
                        ) ? (
                        <div className="text-red-600 text-sm">
                          This category requires a sub-subcategory selection
                        </div>
                      ) : (
                        <div className="text-gray-500 text-sm">
                          No sub-subcategories available. Level-2 selection is
                          valid.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                  {selectedLevel1
                    ? "Select a subcategory"
                    : "Select a main category first"}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={!isValidSelection || loading}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {loading ? "Confirming..." : "Confirm Selection"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
